/**
 * ExpenseMapper - Responsável por aplicar mapeamentos configurados nos dados do Concur
 * antes de enviá-los para o DynamoDB/SQS
 *
 * Versão Front-End (JavaScript puro)
 */

class ExpenseMapper {
  /**
   * Aplica o mapeamento configurado aos dados da transação
   * @param {Object} data - Dados da transação do Concur
   * @param {Array|Object} mappingRules - Regras de mapeamento configuradas (array ou {header, lines, sapFields})
   * @param {String} docType - Tipo de documento ('nfe-expense', 'nfe-cash', 'lc-expense', 'lc-cash')
   * @returns {Object} - Dados mapeados
   */
  static applyMapping(data, mappingRules, docType) {
    // Suporte para formato antigo (array) e novo (objeto com header/lines)
    let headerRules = [];
    let lineRules = [];

    if (Array.isArray(mappingRules)) {
      // Formato antigo: array simples
      headerRules = mappingRules;
    } else if (mappingRules && typeof mappingRules === 'object') {
      // Formato novo: objeto com header/lines/sapFields
      headerRules = mappingRules.header || [];
      lineRules = mappingRules.lines || [];
    }

    if (headerRules.length === 0 && lineRules.length === 0) {
      return data;
    }

    const mappedData = JSON.parse(JSON.stringify(data)); // Deep clone

    // Aplica mapeamentos do header (campos simples)
    for (const rule of headerRules) {
      try {
        const { from, to, cond, conditional } = rule;

        if (!from || !to) continue;

        // Obtém o valor do campo de origem
        const sourceValue = this.getNestedValue(data, from);

        if (sourceValue === undefined || sourceValue === null) continue;

        // Aplica condições se existirem
        let finalValue = sourceValue;
        if (cond) {
          finalValue = this.applyCondition(sourceValue, cond);
        }

        // Define o valor no campo de destino
        this.setNestedValue(mappedData, to, finalValue);

        console.log(`Mapped (header): ${from} = ${sourceValue} → ${to} = ${finalValue}`);
      } catch (error) {
        console.error(
          `Error applying header mapping rule ${JSON.stringify(rule)}: ${error.message}`
        );
      }
    }

    // Aplica mapeamentos das linhas (arrays)
    this.applyLineMapping(mappedData, data, lineRules);

    return mappedData;
  }

  /**
   * Aplica mapeamento de arrays (expenseEntry → DocumentLines/JournalEntryLines)
   * @param {Object} mappedData - Dados já mapeados (será modificado)
   * @param {Object} sourceData - Dados originais (leitura)
   * @param {Array} lineRules - Regras de mapeamento de linhas
   */
  static applyLineMapping(mappedData, sourceData, lineRules) {
    if (!lineRules || lineRules.length === 0) return;

    for (const rule of lineRules) {
      try {
        const { from, to, cond, conditional, condition } = rule;

        if (!from || !to) continue;

        // Verifica se é mapeamento de array ([*])
        const isArrayMapping = from.includes('[*]') || to.includes('[*]');

        if (!isArrayMapping) {
          // Mapeamento simples de campo
          const sourceValue = this.getNestedValue(sourceData, from);
          if (sourceValue !== undefined && sourceValue !== null) {
            let finalValue = sourceValue;
            if (cond) {
              finalValue = this.applyCondition(sourceValue, cond);
            }
            this.setNestedValue(mappedData, to, finalValue);
          }
          continue;
        }

        // Extrai o path do array de origem e destino
        const fromParts = from.split('[*]');
        const toParts = to.split('[*]');

        if (fromParts.length !== 2 || toParts.length !== 2) {
          console.warn(`Invalid array mapping: ${from} → ${to}`);
          continue;
        }

        const sourceArrayPath = fromParts[0]; // ex: "document.expenseEntry"
        const sourceFieldPath = fromParts[1].substring(1); // ex: "entryApprovedAmount" (remove o ponto inicial)
        const targetArrayPath = toParts[0]; // ex: "document.DocumentLines"
        const targetFieldPath = toParts[1].substring(1); // ex: "Price"

        // Obtém o array de origem
        const sourceArray = this.getNestedValue(sourceData, sourceArrayPath);

        if (!sourceArray || !Array.isArray(sourceArray)) {
          console.warn(`Source array not found: ${sourceArrayPath}`);
          continue;
        }

        // Garante que o array de destino existe
        let targetArray = this.getNestedValue(mappedData, targetArrayPath);
        if (!targetArray) {
          this.setNestedValue(mappedData, targetArrayPath, []);
          targetArray = this.getNestedValue(mappedData, targetArrayPath);
        }

        // Itera sobre cada item do array de origem
        sourceArray.forEach((sourceItem, index) => {
          // Garante que o item de destino existe
          if (!targetArray[index]) {
            targetArray[index] = {};
          }

          // Verifica se é campo condicional
          if (conditional) {
            // Se for condicional, só aplica se a condição for verdadeira
            if (condition) {
              // Avalia a condição (ex: "document.employeeCurrencyAlphaCode === 'BRL'")
              const shouldApply = this.evaluateCondition(sourceData, condition);
              if (!shouldApply) return;
            } else {
              // Se conditional=true mas não tem condition, verifica se o campo existe
              const sourceValue = sourceFieldPath ? sourceItem[sourceFieldPath] : sourceItem;
              if (sourceValue === undefined || sourceValue === null || sourceValue === '') return;
            }
          }

          // Obtém o valor do campo de origem
          let sourceValue;
          if (sourceFieldPath) {
            sourceValue = this.getNestedValue(sourceItem, sourceFieldPath);
          } else {
            sourceValue = sourceItem;
          }

          if (sourceValue === undefined || sourceValue === null) return;

          // Aplica condições se existirem
          let finalValue = sourceValue;
          if (cond) {
            finalValue = this.applyCondition(sourceValue, cond);
          }

          // Define o valor no campo de destino
          if (targetFieldPath) {
            this.setNestedValue(targetArray[index], targetFieldPath, finalValue);
          } else {
            targetArray[index] = finalValue;
          }

          console.log(`Mapped (line ${index}): ${from} = ${sourceValue} → ${to} = ${finalValue}`);
        });

      } catch (error) {
        console.error(
          `Error applying line mapping rule ${JSON.stringify(rule)}: ${error.message}`
        );
      }
    }
  }

  /**
   * Avalia uma condição string (ex: "document.employeeCurrencyAlphaCode === 'BRL'")
   * @param {Object} data - Dados para avaliação
   * @param {String} condition - Condição a avaliar
   * @returns {Boolean} - Resultado da avaliação
   */
  static evaluateCondition(data, condition) {
    try {
      // Extrai o path e o valor esperado
      const match = condition.match(/(.+?)\s*(===|!==|==|!=)\s*['"](.+)['"]/);
      if (!match) return false;

      const [, path, operator, expectedValue] = match;
      const actualValue = this.getNestedValue(data, path.trim());

      switch (operator) {
        case '===':
        case '==':
          return actualValue == expectedValue;
        case '!==':
        case '!=':
          return actualValue != expectedValue;
        default:
          return false;
      }
    } catch (error) {
      console.error(`Error evaluating condition: ${condition}`, error);
      return false;
    }
  }

  /**
   * Obtém um valor de um objeto usando notação de caminho (ex: "document.employee.employeeId")
   * @param {Object} obj - Objeto fonte
   * @param {String} path - Caminho do campo
   * @returns {*} - Valor encontrado ou undefined
   */
  static getNestedValue(obj, path) {
    const keys = path.split(".");
    let value = obj;

    for (const key of keys) {
      // Suporta acesso a arrays com notação [index]
      const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
      if (arrayMatch) {
        const arrayKey = arrayMatch[1];
        const index = parseInt(arrayMatch[2]);
        value = value?.[arrayKey]?.[index];
      } else {
        value = value?.[key];
      }

      if (value === undefined || value === null) {
        return undefined;
      }
    }

    return value;
  }

  /**
   * Define um valor em um objeto usando notação de caminho
   * @param {Object} obj - Objeto destino
   * @param {String} path - Caminho do campo
   * @param {*} value - Valor a ser definido
   */
  static setNestedValue(obj, path, value) {
    const keys = path.split(".");
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];

      // Suporta criação de estrutura de arrays
      const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
      if (arrayMatch) {
        const arrayKey = arrayMatch[1];
        const index = parseInt(arrayMatch[2]);

        if (!current[arrayKey]) {
          current[arrayKey] = [];
        }
        if (!current[arrayKey][index]) {
          current[arrayKey][index] = {};
        }
        current = current[arrayKey][index];
      } else {
        if (!current[key]) {
          current[key] = {};
        }
        current = current[key];
      }
    }

    const lastKey = keys[keys.length - 1];
    const arrayMatch = lastKey.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const arrayKey = arrayMatch[1];
      const index = parseInt(arrayMatch[2]);
      if (!current[arrayKey]) {
        current[arrayKey] = [];
      }
      current[arrayKey][index] = value;
    } else {
      current[lastKey] = value;
    }
  }

  /**
   * Aplica condições ao valor
   * @param {*} value - Valor original
   * @param {Object} condition - Objeto de condição
   * @returns {*} - Valor após aplicar a condição
   */
  static applyCondition(value, condition) {
    const { operator, equal, ifValue, elseValue, values } = condition;

    switch (operator) {
      case "if":
        // Condição IF: se valor == equal, retorna ifValue, senão elseValue
        return value == equal ? ifValue : elseValue;

      case "array":
        // Mapeamento de array: procura o valor no array de mapeamento
        if (values && Array.isArray(values)) {
          const mapping = values.find((v) => v.from == value);
          return mapping ? mapping.to : value;
        }
        return value;

      default:
        return value;
    }
  }

  /**
   * Determina o tipo de documento baseado nas configurações da empresa
   * @param {Object} empresaConfig - Configuração da empresa
   * @param {Object} transactionData - Dados da transação
   * @returns {String} - Tipo de documento ('nfe' ou 'lc')
   */
  static getDocumentType(empresaConfig, transactionData) {
    const { documento } = empresaConfig;

    // documento 18 = Nota Fiscal de Entrada
    // documento 30 = Lançamento Contábil (LCM)
    if (documento == 18) {
      return "nfe";
    } else if (documento == 30) {
      return "lc";
    }

    return "nfe"; // default
  }

  /**
   * Determina o tipo de transação (expense ou cash)
   * @param {Object} transactionData - Dados da transação
   * @returns {String} - Tipo de transação ('expense' ou 'cash')
   */
  static getTransactionType(transactionData) {
    const { cashAdvanceApplication } = transactionData.document;

    // Se possui cashAdvanceApplication, é uma transação de Cash (Adiantamento)
    // Senão, é uma transação de Expense (Despesa)
    if (cashAdvanceApplication && cashAdvanceApplication.length > 0) {
      return "cash";
    }

    return "expense";
  }

  /**
   * Aplica o mapeamento completo para uma transação
   * @param {Object} data - Dados da transação
   * @param {Object} concurConfig - Configuração do Concur
   * @param {Object} empresaConfig - Configuração da empresa
   * @returns {Object} - Dados mapeados
   */
  static mapTransaction(data, concurConfig, empresaConfig) {
    const docType = this.getDocumentType(empresaConfig, data);
    const transType = this.getTransactionType(data);

    // Constrói a chave de mapeamento (ex: "mapping-nfe-expense", "mapping-lc-cash")
    const mappingKey = `mapping-${docType}-${transType}`;

    console.log(`Applying mapping: ${mappingKey}`);

    // Obtém as regras de mapeamento da configuração do Concur
    const mappingRules = concurConfig[mappingKey] || [];

    if (mappingRules.length === 0) {
      console.log(`No mapping rules found for ${mappingKey}`);
      return data;
    }

    console.log(`Found ${mappingRules.length} mapping rules for ${mappingKey}`);

    // Aplica o mapeamento
    const mappedData = this.applyMapping(data, mappingRules, mappingKey);

    return mappedData;
  }
}

export default ExpenseMapper;
